import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Euro, TrendingDown, Percent, Receipt, Wallet, Users, Check, Pencil, Briefcase, Calculator } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatEuro, formatPct } from "@/lib/format";
import { resolveKpiLabel } from "@/lib/kpiLabels";

const cards = [
  { key: "total_valor", label: "Faturação Total", icon: Euro, fmt: formatEuro, accent: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-950/40", testid: "kpi-total-revenue" },
  { key: "total_custos", label: "Custos Totais", icon: TrendingDown, fmt: formatEuro, accent: "text-rose-600 dark:text-rose-400", bg: "bg-rose-50 dark:bg-rose-950/40", testid: "kpi-total-costs" },
  { key: "total_custos_indiretos", label: "Custos Indiretos", icon: Receipt, fmt: formatEuro, accent: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-950/40", testid: "kpi-total-indirect" },
  { key: "total_margem", label: "Margem Total", icon: Euro, fmt: formatEuro, accent: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/40", testid: "kpi-total-margin" },
  { key: "total_lucro_mundideias", label: "Lucro Mundideias", icon: Briefcase, fmt: formatEuro, accent: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-950/40", testid: "kpi-lucro-mundideias" },
  { key: "total_mundideias", label: "Mundideias + Custos Ind.", icon: Wallet, fmt: formatEuro, accent: "text-violet-600 dark:text-violet-400", bg: "bg-violet-50 dark:bg-violet-950/40", testid: "kpi-mundideias-ci" },
  { key: "avg_margin_pct", label: "Margem Média", icon: Percent, fmt: formatPct, accent: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/40", testid: "kpi-avg-margin" },
];

function EquipaCard({ value, onSave, label }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? 0);
  useEffect(() => setVal(value ?? 0), [value]);

  const save = () => {
    onSave(parseFloat(val) || 0);
    setEditing(false);
  };

  return (
    <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-4 sm:p-5" data-testid="kpi-total-equipa">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-primary">{label}</span>
        <span className="h-8 w-8 rounded-lg flex items-center justify-center bg-primary/10">
          <Users className="h-4 w-4 text-primary" />
        </span>
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} autoFocus
            className="h-9" data-testid="total-equipa-input" onKeyDown={(e) => e.key === "Enter" && save()} />
          <button onClick={save} className="p-1.5 text-emerald-600 hover:bg-muted rounded" data-testid="total-equipa-save">
            <Check className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 cursor-pointer group" onClick={() => setEditing(true)} data-testid="total-equipa-display">
          <span className="font-mono-num text-xl sm:text-2xl font-bold tracking-tight">{formatEuro(value)}</span>
          <Pencil className="h-3.5 w-3.5 opacity-40 group-hover:opacity-80 transition-opacity" />
        </div>
      )}
    </div>
  );
}

export default function KPIOverview({ stats, totalEquipa, onSaveTotalEquipa, labels }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div key={c.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="rounded-xl border border-border bg-card p-4 sm:p-5" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{resolveKpiLabel(labels, c.key)}</span>
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.bg}`}>
                <Icon className={`h-4 w-4 ${c.accent}`} />
              </span>
            </div>
            <div className="font-mono-num text-xl sm:text-2xl font-bold tracking-tight">
              {stats ? c.fmt(stats[c.key]) : "—"}
            </div>
            {c.key === "total_valor" && stats && (
              <div className="text-xs text-muted-foreground mt-1">{stats.count} processo(s)</div>
            )}
          </motion.div>
        );
      })}
      {stats && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-border bg-card p-4 sm:p-5" data-testid="kpi-resultado-liquido">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{resolveKpiLabel(labels, "resultado_liquido")}</span>
            <span className="h-8 w-8 rounded-lg flex items-center justify-center bg-teal-50 dark:bg-teal-950/40">
              <Calculator className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            </span>
          </div>
          <div className="font-mono-num text-xl sm:text-2xl font-bold tracking-tight">
            {formatEuro((stats.total_mundideias || 0) - (totalEquipa || 0))}
          </div>
        </motion.div>
      )}
      <EquipaCard value={totalEquipa} onSave={onSaveTotalEquipa} label={resolveKpiLabel(labels, "total_equipa")} />
    </div>
  );
}
